<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Audit Log</h1>
        <div>
          <button class="btn btn-outline-secondary btn-sm mr-2" @click="exportLogs" :disabled="!auditLogs.length">
            <b-icon icon="download"></b-icon> Export CSV
          </button>
          <button class="btn btn-outline-primary btn-sm" @click="loadAuditLogs" :disabled="loading">
            <b-spinner small v-if="loading"></b-spinner>
            <b-icon v-else icon="arrow-clockwise"></b-icon> Refresh
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row">
            <div class="col-md-3 mb-2">
              <label class="small text-muted mb-1">Module</label>
              <b-form-select v-model="filters.module" @change="onModuleChange" size="sm">
                <option value="">All Modules</option>
                <option v-for="mod in modules" :key="mod.key" :value="mod.key">{{ mod.label }}</option>
              </b-form-select>
            </div>
            <div class="col-md-3 mb-2">
              <label class="small text-muted mb-1">Action</label>
              <b-form-select v-model="filters.action" @change="loadAuditLogs" size="sm">
                <option value="">All Actions</option>
                <option v-for="action in filteredActions" :key="action.key" :value="action.key">{{ action.label }}</option>
              </b-form-select>
            </div>
            <div class="col-md-2 mb-2">
              <label class="small text-muted mb-1">Admin</label>
              <b-form-select v-model="filters.admin_id" @change="loadAuditLogs" size="sm">
                <option value="">All Admins</option>
                <option v-for="admin in adminUsers" :key="admin.id" :value="admin.id">{{ admin.name }}</option>
              </b-form-select>
            </div>
            <div class="col-md-2 mb-2">
              <label class="small text-muted mb-1">From</label>
              <b-form-input type="date" v-model="filters.date_from" @change="loadAuditLogs" size="sm"></b-form-input>
            </div>
            <div class="col-md-2 mb-2">
              <label class="small text-muted mb-1">To</label>
              <b-form-input type="date" v-model="filters.date_to" @change="loadAuditLogs" size="sm"></b-form-input>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-4">
              <b-input-group size="sm">
                <b-form-input
                  v-model="filters.search"
                  placeholder="Search by target name, email, or description..."
                  @keyup.enter="loadAuditLogs"
                ></b-form-input>
                <b-input-group-append>
                  <b-button variant="primary" @click="loadAuditLogs" :disabled="loading">
                    <b-icon icon="search"></b-icon>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
            <div class="col-md-8 text-right">
              <button class="btn btn-link btn-sm text-muted" @click="clearFilters">Clear Filters</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row mb-4" v-if="summary.total > 0">
        <div class="col-md-3">
          <div class="app-card shadow-sm p-3 text-center">
            <div class="text-muted small">Total Entries</div>
            <div class="h4 mb-0">{{ summary.total.toLocaleString() }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="app-card shadow-sm p-3 text-center">
            <div class="text-muted small">Today</div>
            <div class="h4 mb-0">{{ summary.today.toLocaleString() }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="app-card shadow-sm p-3 text-center">
            <div class="text-muted small">Active Admins</div>
            <div class="h4 mb-0">{{ summary.activeAdmins }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="app-card shadow-sm p-3 text-center">
            <div class="text-muted small">Most Active Module</div>
            <div class="h6 mb-0">{{ summary.topModule || '—' }}</div>
          </div>
        </div>
      </div>

      <!-- Logs Table -->
      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div v-if="loading" class="text-center my-4">
            <b-spinner variant="primary"></b-spinner>
            <div class="mt-2 text-muted">Loading audit logs...</div>
          </div>

          <b-table
            v-else
            :items="auditLogs"
            :fields="tableFields"
            responsive
            striped
            hover
            show-empty
            :tbody-tr-class="rowClass"
          >
            <template #empty>
              <div class="text-center text-muted py-4">No audit logs found matching your filters.</div>
            </template>
            <template #cell(module)="data">
              <b-badge :variant="moduleVariant(data.value)">{{ formatModule(data.value) }}</b-badge>
            </template>
            <template #cell(action)="data">
              <span class="text-dark">{{ formatAction(data.value) }}</span>
            </template>
            <template #cell(admin_name)="data">
              <span>{{ data.value || 'System' }}</span>
              <br v-if="data.item.admin_role">
              <small v-if="data.item.admin_role" class="text-muted">{{ data.item.admin_role }}</small>
            </template>
            <template #cell(target)="data">
              <span v-if="data.item.target_name">{{ data.item.target_name }}</span>
              <span v-else-if="data.item.target_id" class="text-muted">ID: {{ data.item.target_id }}</span>
              <span v-else class="text-muted">—</span>
              <br v-if="data.item.target_type">
              <small class="text-muted">{{ data.item.target_type }}</small>
            </template>
            <template #cell(description)="data">
              <small>{{ data.value || formatDetails(data.item.details) }}</small>
            </template>
            <template #cell(ip_address)="data">
              <small class="text-muted">{{ data.value || '—' }}</small>
            </template>
            <template #cell(created_at)="data">
              <small>{{ formatDateTime(data.value) }}</small>
            </template>
            <template #cell(actions)="data">
              <b-button size="sm" variant="outline-info" @click="viewDetails(data.item)" title="View Details">
                <b-icon icon="eye"></b-icon>
              </b-button>
            </template>
          </b-table>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center" v-if="totalRows > perPage">
            <small class="text-muted">
              Showing {{ ((currentPage - 1) * perPage) + 1 }}–{{ Math.min(currentPage * perPage, totalRows) }} of {{ totalRows.toLocaleString() }}
            </small>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              size="sm"
              @change="onPageChange"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Detail Modal -->
      <b-modal id="audit-detail-modal" title="Audit Log Detail" size="lg" ok-only ok-title="Close" scrollable>
        <div v-if="selectedLog">
          <div class="row mb-3">
            <div class="col-md-6">
              <p class="mb-1"><strong>Admin:</strong> {{ selectedLog.admin_name || 'System' }}</p>
              <p class="mb-1"><strong>Role:</strong> {{ selectedLog.admin_role || '—' }}</p>
              <p class="mb-1"><strong>IP Address:</strong> {{ selectedLog.ip_address || '—' }}</p>
            </div>
            <div class="col-md-6">
              <p class="mb-1"><strong>Module:</strong> <b-badge :variant="moduleVariant(selectedLog.module)">{{ formatModule(selectedLog.module) }}</b-badge></p>
              <p class="mb-1"><strong>Action:</strong> {{ formatAction(selectedLog.action) }}</p>
              <p class="mb-1"><strong>Date/Time:</strong> {{ formatDateTime(selectedLog.created_at) }}</p>
            </div>
          </div>
          <hr>
          <div class="mb-3">
            <strong>Target:</strong>
            <span v-if="selectedLog.target_name"> {{ selectedLog.target_name }}</span>
            <span v-if="selectedLog.target_type"> ({{ selectedLog.target_type }})</span>
            <span v-if="selectedLog.target_id"> — ID: {{ selectedLog.target_id }}</span>
          </div>
          <div v-if="selectedLog.description" class="mb-3">
            <strong>Description:</strong>
            <p class="mb-0">{{ selectedLog.description }}</p>
          </div>
          <div v-if="selectedLog.details">
            <strong>Details:</strong>
            <div class="bg-light rounded p-3 mt-1" style="max-height: 300px; overflow-y: auto;">
              <pre class="mb-0" style="white-space: pre-wrap; font-size: 0.85rem;">{{ formatDetailsJson(selectedLog.details) }}</pre>
            </div>
          </div>
          <div v-if="selectedLog.old_values || selectedLog.new_values" class="mt-3">
            <strong>Changes:</strong>
            <div class="row mt-1">
              <div class="col-md-6" v-if="selectedLog.old_values">
                <div class="small text-muted mb-1">Before</div>
                <div class="bg-light rounded p-2" style="max-height: 200px; overflow-y: auto;">
                  <pre class="mb-0" style="white-space: pre-wrap; font-size: 0.8rem;">{{ formatDetailsJson(selectedLog.old_values) }}</pre>
                </div>
              </div>
              <div class="col-md-6" v-if="selectedLog.new_values">
                <div class="small text-muted mb-1">After</div>
                <div class="bg-light rounded p-2" style="max-height: 200px; overflow-y: auto;">
                  <pre class="mb-0" style="white-space: pre-wrap; font-size: 0.8rem;">{{ formatDetailsJson(selectedLog.new_values) }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      auditLogs: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      selectedLog: null,
      adminUsers: [],
      filters: {
        module: "",
        action: "",
        admin_id: "",
        date_from: "",
        date_to: "",
        search: "",
      },
      summary: {
        total: 0,
        today: 0,
        activeAdmins: 0,
        topModule: "",
      },
      modules: [
        { key: "auth", label: "Authentication" },
        { key: "admissions", label: "Admissions" },
        { key: "academics", label: "Academics" },
        { key: "students", label: "Students" },
        { key: "bursary", label: "Bursary" },
        { key: "payments", label: "Payments" },
        { key: "settings", label: "Settings" },
        { key: "helpdesk", label: "Helpdesk" },
      ],
      allActions: [
        { key: "login", module: "auth", label: "Login" },
        { key: "logout", module: "auth", label: "Logout" },
        { key: "failed_login", module: "auth", label: "Failed Login" },

        { key: "approve_admission", module: "admissions", label: "Approve Admission" },
        { key: "deny_admission", module: "admissions", label: "Deny Admission" },
        { key: "recommend_application", module: "admissions", label: "Recommend Application" },
        { key: "reject_application", module: "admissions", label: "Reject Application" },
        { key: "reset_application_for_edit", module: "admissions", label: "Reset Application for Edit" },
        { key: "reset_pg_action", module: "admissions", label: "Reset PG Action" },
        { key: "reset_recommendation", module: "admissions", label: "Reset Recommendation" },
        { key: "send_email", module: "admissions", label: "Send Email" },
        { key: "download_applications", module: "admissions", label: "Download Applications" },

        { key: "create_college", module: "academics", label: "Create College" },
        { key: "update_college", module: "academics", label: "Update College" },
        { key: "create_department", module: "academics", label: "Create Department" },
        { key: "update_department", module: "academics", label: "Update Department" },
        { key: "create_programme", module: "academics", label: "Create Programme" },
        { key: "update_programme", module: "academics", label: "Update Programme" },
        { key: "bulk_update_programmes", module: "academics", label: "Bulk Update Programmes" },
        { key: "create_course", module: "academics", label: "Create Course" },
        { key: "update_course", module: "academics", label: "Update Course" },
        { key: "delete_course", module: "academics", label: "Delete Course" },
        { key: "upload_courses", module: "academics", label: "Upload Courses" },
        { key: "create_curriculum", module: "academics", label: "Create Curriculum" },
        { key: "update_curriculum", module: "academics", label: "Update Curriculum" },
        { key: "delete_curriculum", module: "academics", label: "Delete Curriculum" },
        { key: "upload_curriculum", module: "academics", label: "Upload Curriculum" },
        { key: "create_course_registration", module: "academics", label: "Create Course Registration" },
        { key: "add_course_to_registration", module: "academics", label: "Add Course to Registration" },
        { key: "remove_registered_course", module: "academics", label: "Remove Registered Course" },
        { key: "approve_course_registration", module: "academics", label: "Approve Course Registration" },
        { key: "disapprove_course_registration", module: "academics", label: "Disapprove Course Registration" },
        { key: "create_result", module: "academics", label: "Create Result" },
        { key: "update_result", module: "academics", label: "Update Result" },
        { key: "delete_result", module: "academics", label: "Delete Result" },
        { key: "upload_results", module: "academics", label: "Upload Results" },

        { key: "create_student", module: "students", label: "Create Student" },
        { key: "update_student", module: "students", label: "Update Student" },
        { key: "delete_student", module: "students", label: "Delete Student" },
        { key: "toggle_student_lock", module: "students", label: "Toggle Student Lock" },
        { key: "create_student_degree", module: "students", label: "Create Student Degree" },
        { key: "update_student_degree", module: "students", label: "Update Student Degree" },
        { key: "delete_student_degree", module: "students", label: "Delete Student Degree" },
        { key: "create_student_calendar", module: "students", label: "Add Student Semester" },
        { key: "delete_student_calendar", module: "students", label: "Remove Student Semester" },
        { key: "preload_matric_number", module: "students", label: "Preload Matric Number" },
        { key: "download_student_data", module: "students", label: "Download Student Data" },

        { key: "create_billing", module: "bursary", label: "Create Billing" },
        { key: "update_billing", module: "bursary", label: "Update Billing" },
        { key: "create_fee_schedule", module: "bursary", label: "Create Fee Schedule" },
        { key: "update_fee_schedule", module: "bursary", label: "Update Fee Schedule" },
        { key: "delete_fee_schedule", module: "bursary", label: "Delete Fee Schedule" },
        { key: "bulk_delete_fee_schedule", module: "bursary", label: "Bulk Delete Fee Schedule" },
        { key: "delete_session_fee_schedule", module: "bursary", label: "Delete Session Fee Schedule" },
        { key: "clone_fee_schedule", module: "bursary", label: "Clone Fee Schedule" },
        { key: "create_admission_fee", module: "bursary", label: "Create Admission Fee" },
        { key: "update_admission_fee", module: "bursary", label: "Update Admission Fee" },
        { key: "create_service_type", module: "bursary", label: "Create Service Type" },
        { key: "update_service_type", module: "bursary", label: "Update Service Type" },
        { key: "create_special_payment", module: "bursary", label: "Create Special Payment" },
        { key: "update_special_payment", module: "bursary", label: "Update Special Payment" },
        { key: "create_fee_exemption", module: "bursary", label: "Create Fee Exemption" },
        { key: "delete_fee_exemption", module: "bursary", label: "Delete Fee Exemption" },
        { key: "delete_student_transaction", module: "bursary", label: "Delete Student Transaction" },
        { key: "assign_bill", module: "bursary", label: "Assign Bill" },

        { key: "requery_payment", module: "payments", label: "Requery Payment" },

        { key: "create_session", module: "settings", label: "Create Session" },
        { key: "update_session", module: "settings", label: "Update Session" },
        { key: "approve_access_request", module: "settings", label: "Approve Access Request" },
        { key: "deny_access_request", module: "settings", label: "Deny Access Request" },

        { key: "update_applicant_contact", module: "helpdesk", label: "Update Applicant Contact" },
        { key: "reset_applicant_password", module: "helpdesk", label: "Reset Applicant Password" },
        { key: "reset_student_password", module: "helpdesk", label: "Reset Student Password" },
        { key: "resend_admission_email", module: "helpdesk", label: "Resend Admission Email" },
        { key: "create_student_record", module: "helpdesk", label: "Create Student Record" },
      ],
      tableFields: [
        { key: "created_at", label: "Date/Time", sortable: true, thStyle: { width: "140px" } },
        { key: "admin_name", label: "Admin", sortable: true },
        { key: "module", label: "Module", sortable: true, thStyle: { width: "110px" } },
        { key: "action", label: "Action", sortable: true },
        { key: "target", label: "Target", sortable: false },
        { key: "description", label: "Description", sortable: false },
        { key: "ip_address", label: "IP", sortable: true, thStyle: { width: "110px" } },
        { key: "actions", label: "", thStyle: { width: "50px" } },
      ],
    };
  },

  computed: {
    filteredActions() {
      if (!this.filters.module) return this.allActions;
      return this.allActions.filter((a) => a.module === this.filters.module);
    },
  },

  methods: {
    loadAuditLogs() {
      this.loading = true;
      var params = { page: this.currentPage, per_page: this.perPage };
      if (this.filters.module) params.module = this.filters.module;
      if (this.filters.action) params.action = this.filters.action;
      if (this.filters.admin_id) params.admin_id = this.filters.admin_id;
      if (this.filters.date_from) params.date_from = this.filters.date_from;
      if (this.filters.date_to) params.date_to = this.filters.date_to;
      if (this.filters.search) params.search = this.filters.search;

      axios
        .get("admin/audit-logs", { params: params })
        .then((res) => {
          var result = res.data.data || res.data;
          if (result.data) {
            this.auditLogs = result.data;
            this.totalRows = result.total || result.data.length;
          } else if (Array.isArray(result)) {
            this.auditLogs = result;
            this.totalRows = result.length;
          } else {
            this.auditLogs = [];
            this.totalRows = 0;
          }
          if (res.data.summary) {
            this.summary = res.data.summary;
          }
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load audit logs" });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadAdminUsers() {
      axios
        .get("admin/admin-users")
        .then((res) => {
          var users = res.data.data || res.data || [];
          this.adminUsers = users.map(function (u) {
            return { id: u.id, name: [u.surname, u.first_name || u.firstname].filter(Boolean).join(" ") || u.email || "Admin #" + u.id };
          });
        })
        .catch(() => {});
    },

    onModuleChange() {
      this.filters.action = "";
      this.loadAuditLogs();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadAuditLogs();
    },

    clearFilters() {
      this.filters = { module: "", action: "", admin_id: "", date_from: "", date_to: "", search: "" };
      this.currentPage = 1;
      this.loadAuditLogs();
    },

    viewDetails(log) {
      this.selectedLog = log;
      this.$bvModal.show("audit-detail-modal");
    },

    moduleVariant(mod) {
      var map = {
        auth: "dark",
        admissions: "info",
        academics: "primary",
        students: "success",
        bursary: "warning",
        payments: "danger",
        settings: "secondary",
        helpdesk: "light",
      };
      return map[mod] || "secondary";
    },

    formatModule(mod) {
      if (!mod) return "—";
      return mod.charAt(0).toUpperCase() + mod.slice(1);
    },

    formatAction(action) {
      if (!action) return "";
      var found = this.allActions.find(function (a) { return a.key === action; });
      if (found) return found.label;
      return action.replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    },

    formatDetails(details) {
      if (!details) return "";
      if (typeof details === "string") {
        try { details = JSON.parse(details); } catch (e) { return details; }
      }
      return Object.entries(details)
        .map(function (entry) { return entry[0] + ": " + entry[1]; })
        .join(", ");
    },

    formatDetailsJson(details) {
      if (!details) return "";
      if (typeof details === "string") {
        try { return JSON.stringify(JSON.parse(details), null, 2); } catch (e) { return details; }
      }
      return JSON.stringify(details, null, 2);
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
        " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    },

    rowClass(item) {
      if (!item) return "";
      if (item.action && (item.action.indexOf("delete") > -1 || item.action.indexOf("deny") > -1)) return "table-danger-light";
      return "";
    },

    exportLogs() {
      if (!this.auditLogs.length) return;
      var headers = ["Date/Time", "Admin", "Module", "Action", "Target", "Description", "IP Address"];
      var rows = this.auditLogs.map((log) => {
        return [
          this.formatDateTime(log.created_at),
          log.admin_name || "System",
          this.formatModule(log.module),
          this.formatAction(log.action),
          (log.target_name || "") + (log.target_type ? " (" + log.target_type + ")" : ""),
          log.description || this.formatDetails(log.details),
          log.ip_address || "",
        ].map((v) => '"' + String(v).replace(/"/g, '""') + '"');
      });
      var csv = [headers.join(",")].concat(rows.map((r) => r.join(","))).join("\n");
      var blob = new Blob([csv], { type: "text/csv" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = "audit-log-" + new Date().toISOString().slice(0, 10) + ".csv";
      a.click();
      URL.revokeObjectURL(url);
    },
  },

  created() {
    this.loadAuditLogs();
    this.loadAdminUsers();
  },
};
</script>

<style scoped>
.table-danger-light {
  background-color: rgba(220, 53, 69, 0.05) !important;
}
</style>
